/**
 * The employee Excel import (PRD §10.1, PLAN.md D-24/D-25): drop a workbook, read the verdicts,
 * commit.
 *
 * The file is parsed on the server, never in the browser, so preview and commit judge the rows by
 * exactly the same rules. The import creates people and assigns them; it never edits master data
 * of someone who already exists — differing values are shown and then ignored.
 * Projektaustrittsdatum is applied only on create; existing people set it in the assignment
 * edit dialog.
 */

import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Download, Send, Upload } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DateField } from "@/components/ui/date-field";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { describeError } from "@/lib/errors";
import { formatDate, formatPercent } from "@/lib/format";
import { toastApiError } from "@/lib/rate-limit";
import { useCommitImport, useInvitePeople, usePreviewImport } from "@/lib/tenancy";
import { berlinToday, entryDateMaxHorizon } from "@/shared/dates";
import type {
  ImportPreview,
  ImportPreviewRow,
  ImportResult,
  ImportVerdict,
  ProjectSummary,
} from "@/shared/schemas/tenancy";

const VERDICT_ORDER: Record<ImportVerdict, number> = { skip: 0, assign: 1, create: 2 };

const VERDICT_BADGE_CLASS: Record<ImportVerdict, string> = {
  skip: "cursor-help border-amber-500/40 bg-amber-500/15 text-amber-900 hover:bg-amber-500/20",
  assign: "border-sky-500/40 bg-sky-500/15 text-sky-900 hover:bg-sky-500/20",
  create: "border-emerald-500/40 bg-emerald-500/15 text-emerald-900 hover:bg-emerald-500/20",
};

function sortedPreviewRows(rows: ImportPreviewRow[]): ImportPreviewRow[] {
  return [...rows].sort((a, b) => {
    const byVerdict = VERDICT_ORDER[a.verdict] - VERDICT_ORDER[b.verdict];
    if (byVerdict !== 0) return byVerdict;
    return a.rowNumber - b.rowNumber;
  });
}

export function ImportDialog({
  open,
  onOpenChange,
  companyId,
  projects,
  onImported,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
  projects: ProjectSummary[];
  onImported: (result: ImportResult) => void;
}) {
  const { t } = useTranslation();
  const fileInput = useRef<HTMLInputElement>(null);
  const closed = useRef(false);

  const [projectId, setProjectId] = useState(projects[0]?.id ?? "");
  const [entryDate, setEntryDate] = useState(berlinToday());
  const [preview, setPreview] = useState<(ImportPreview & { filename: string }) | null>(null);
  const [committed, setCommitted] = useState<ImportResult | null>(null);

  const previewImport = usePreviewImport(projectId);
  const commitImport = useCommitImport(projectId);
  const invite = useInvitePeople(companyId);

  const project = projects.find((entry) => entry.id === projectId);
  const entryHorizon = entryDateMaxHorizon();
  const entryDateMax =
    project?.endDate && project.endDate < entryHorizon ? project.endDate : entryHorizon;
  const importable = (preview?.rows ?? []).filter((row) => row.verdict !== "skip");
  const inviteCount = committed?.inviteableUserIds.length ?? 0;
  const frozen = committed !== null;

  function close(result: ImportResult | null) {
    if (closed.current) return;
    closed.current = true;
    setPreview(null);
    setCommitted(null);
    onOpenChange(false);
    if (result) onImported(result);
  }

  function choose(file: File | undefined) {
    if (!file || !projectId || frozen) return;
    if (entryDate > entryHorizon) {
      toast.error(
        t("errors.ASSIGNMENT_ENTRY_TOO_FAR_IN_FUTURE", {
          horizon_date: entryHorizon,
        }),
      );
      return;
    }
    previewImport.mutate(
      { file, entryDate },
      {
        onSuccess: (data) => setPreview(data),
        onError: (error) => toast.error(describeError(error)),
      },
    );
  }

  function commit() {
    if (!preview) return;
    if (entryDate > entryHorizon) {
      toast.error(
        t("errors.ASSIGNMENT_ENTRY_TOO_FAR_IN_FUTURE", {
          horizon_date: entryHorizon,
        }),
      );
      return;
    }
    commitImport.mutate(
      {
        filename: preview.filename,
        defaultEntryDate: entryDate,
        rows: importable.map((row) => ({
          rowNumber: row.rowNumber,
          firstName: row.firstName,
          lastName: row.lastName,
          email: row.email,
          position: row.position,
          jobDescription: row.jobDescription,
          weeklyHours: row.weeklyHours,
          workdaysPerWeek: row.workdaysPerWeek,
          rdShare: row.rdShare,
          entryDate: row.entryDate,
          leaveDate: row.verdict === "create" ? (row.leaveDate ?? null) : null,
          isCompanyAdmin: row.isCompanyAdmin ?? null,
        })),
      },
      {
        onSuccess: (result) => {
          toast.success(
            t("employees.importSummary", {
              created: result.created,
              assigned: result.assigned,
              skipped: result.skipped,
            }),
          );
          setCommitted(result);
        },
        onError: (error) => toast.error(describeError(error)),
      },
    );
  }

  function sendInvitations() {
    if (!committed || committed.inviteableUserIds.length === 0) return;
    invite.mutate(
      { userIds: committed.inviteableUserIds },
      {
        onSuccess: (data) => {
          const sent = data.results.filter(
            (entry) => entry.status === "queued" || entry.status === "deduped",
          ).length;
          toast.success(t("employees.inviteResult", { sent, skipped: data.results.length - sent }));
          close(committed);
        },
        onError: (error) => toastApiError(error),
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close(committed);
      }}
    >
      <DialogContent className="max-h-[85vh] overflow-x-hidden overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t("employees.importTitle")}</DialogTitle>
          <DialogDescription>
            {t("employees.importBody", { project: project?.name ?? "—" })}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="import-project">{t("employees.importProject")}</Label>
            <Select
              value={projectId}
              onValueChange={(value) => {
                setProjectId(value);
                setPreview(null);
              }}
              disabled={frozen}
            >
              <SelectTrigger id="import-project">
                <SelectValue placeholder={t("employees.importProject")} />
              </SelectTrigger>
              <SelectContent>
                {projects.map((entry) => (
                  <SelectItem key={entry.id} value={entry.id}>
                    {entry.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="import-entry">{t("employees.importEntryDate")}</Label>
            <DateField
              id="import-entry"
              value={entryDate}
              min={project?.startDate}
              max={entryDateMax}
              disabled={frozen}
              onChange={(next) => {
                setEntryDate(next);
                setPreview(null);
              }}
            />
            <p className="text-xs text-muted-foreground">{t("employees.importEntryDateHint")}</p>
          </div>
        </div>
        <p className="text-xs text-muted-foreground" data-testid="import-leave-date-hint">
          {t("employees.importLeaveDateHint")}
        </p>

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" asChild disabled={!projectId || frozen}>
            <a href={`/api/projects/${projectId}/employees/template.xlsx`} download>
              <Download className="size-4" /> {t("employees.importTemplate")}
            </a>
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!projectId || previewImport.isPending || frozen}
            onClick={() => fileInput.current?.click()}
          >
            <Upload className="size-4" />
            {previewImport.isPending ? t("employees.importParsing") : t("employees.importDrop")}
          </Button>
          <input
            ref={fileInput}
            type="file"
            accept=".xlsx"
            className="hidden"
            onChange={(event) => {
              choose(event.target.files?.[0]);
              event.target.value = "";
            }}
          />
        </div>

        {preview && (
          <div className="min-w-0 space-y-3">
            <p className="text-sm text-muted-foreground">
              {t("employees.importPreview", { count: preview.rows.length })}
              {" · "}
              {t("employees.importSummary", {
                created: preview.counts.create,
                assigned: preview.counts.assign,
                skipped: preview.counts.skip,
              })}
            </p>

            {preview.fallbackCompanyAdmin && (
              <p className="text-sm text-muted-foreground" data-testid="import-admin-fallback-note">
                {t("employees.importAdminFallback", {
                  name: preview.fallbackCompanyAdmin.name,
                })}
              </p>
            )}

            {preview.errors.length > 0 && (
              <ul className="space-y-1 text-xs text-destructive">
                {preview.errors.map((error) => (
                  <li key={`${error.rowNumber}-${error.message}`}>
                    {t("employees.importExcelRow", { n: error.rowNumber })}:{" "}
                    {t(`employees.reason_${error.message}`, { defaultValue: error.message })}
                  </li>
                ))}
              </ul>
            )}

            <div className="max-h-72 min-w-0 overflow-x-hidden overflow-y-auto rounded-md border border-border bg-card">
              <TooltipProvider delayDuration={200}>
                <Table className="table-fixed">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-16">{t("employees.importColRow")}</TableHead>
                      <TableHead>{t("employees.colName")}</TableHead>
                      <TableHead>{t("employees.colPosition")}</TableHead>
                      <TableHead>{t("employees.colEmail")}</TableHead>
                      <TableHead className="w-20">{t("employees.colHours")}</TableHead>
                      <TableHead className="w-24">
                        {t("employees.assignRdShareColumnHeader")}
                      </TableHead>
                      <TableHead className="w-28">{t("employees.assignLeaveDate")}</TableHead>
                      <TableHead>{t("employees.importColVerdict")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedPreviewRows(preview.rows).map((row) => {
                      const name = `${row.firstName} ${row.lastName}`.trim();
                      const position = row.position ?? "—";
                      const differsNote =
                        row.differs && row.differs.length > 0
                          ? t("employees.importDiffers", {
                              fields: row.differs.join(", "),
                            })
                          : null;
                      const leaveLabel =
                        row.verdict === "create" && row.leaveDate ? formatDate(row.leaveDate) : "—";
                      return (
                        <TableRow key={row.rowNumber}>
                          <TableCell className="text-muted-foreground">{row.rowNumber}</TableCell>
                          <TableCell className="truncate" title={name}>
                            {name}
                          </TableCell>
                          <TableCell className="truncate text-muted-foreground" title={position}>
                            {position}
                          </TableCell>
                          <TableCell className="truncate text-muted-foreground" title={row.email}>
                            {row.email}
                          </TableCell>
                          <TableCell>{row.weeklyHours}</TableCell>
                          <TableCell>{formatPercent(row.rdShare)}</TableCell>
                          <TableCell
                            className="text-muted-foreground"
                            data-testid="import-leave-date"
                            data-verdict={row.verdict}
                          >
                            {leaveLabel}
                          </TableCell>
                          <TableCell>
                            {row.verdict === "skip" ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    className="inline-flex rounded-md border-0 bg-transparent p-0"
                                    data-testid="import-skip-badge"
                                    data-reason={row.reason ?? ""}
                                  >
                                    <Badge variant="secondary" className={VERDICT_BADGE_CLASS.skip}>
                                      {t("employees.verdict_skip")}
                                    </Badge>
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent
                                  side="top"
                                  className="z-[100] max-w-sm text-pretty"
                                  hideWhenDetached={false}
                                  data-testid="import-skip-reason-tooltip"
                                  data-reason={row.reason ?? ""}
                                >
                                  {row.reason
                                    ? t(`employees.reason_${row.reason}`, {
                                        defaultValue: row.reason,
                                      })
                                    : t("employees.verdict_skip")}
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              <>
                                <Badge
                                  variant="secondary"
                                  className={VERDICT_BADGE_CLASS[row.verdict]}
                                >
                                  {t(`employees.verdict_${row.verdict}`)}
                                </Badge>
                                {differsNote && (
                                  <div
                                    className="mt-1 truncate text-xs text-muted-foreground"
                                    title={differsNote}
                                  >
                                    {differsNote}
                                  </div>
                                )}
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TooltipProvider>
            </div>
          </div>
        )}

        {committed && (
          <p className="text-sm" data-testid="import-commit-summary">
            {t("employees.importSummary", {
              created: committed.created,
              assigned: committed.assigned,
              skipped: committed.skipped,
            })}
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => close(committed)}>
            {t("common.cancel")}
          </Button>
          {committed ? (
            inviteCount > 0 && (
              <Button
                data-testid="import-send-invitation"
                disabled={invite.isPending}
                onClick={sendInvitations}
              >
                <Send className="size-4" />
                {t("employees.inviteMany", { count: inviteCount })}
              </Button>
            )
          ) : (
            <Button onClick={commit} disabled={commitImport.isPending || importable.length === 0}>
              {t("employees.importCommit", { count: importable.length })}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
